import {utcToZonedTime, zonedTimeToUtc} from "date-fns-tz";

/**
 * @hidden
 */
export function inZone(zone: string, fn: (date: Date, ...args: never[]) => number, date: Date, ...args: unknown[]): number;

/**
 * @hidden
 */
export function inZone(zone: string, fn: (date: Date, ...args: never[]) => Date, date: Date, ...args: unknown[]): Date;

/**
 * @hidden
 */
export function inZone(zone: string, fn: (date: Date, ...args: never[]) => Date, date: undefined, ...args: unknown[]): undefined;

/**
 * @hidden
 */
export function inZone(zone: string, fn: (date: Date, ...args: never[]) => Date, date: null, ...args: unknown[]): null;

/**
 * @hidden
 */
export function inZone(zone: string, fn: (date: Date, ...args: never[]) => Date, date: Date | undefined, ...args: unknown[]): Date | undefined;

/**
 * @hidden
 */
export function inZone(zone: string, fn: (date: Date, ...args: never[]) => Date, date: Date | null, ...args: unknown[]): Date | null;

/**
 * @hidden
 */
export function inZone(zone: string, fn: (date: Date, ...args: never[]) => Date, date: Date | undefined | null, ...args: unknown[]): Date | undefined | null;

export function inZone(zone: string, fn: (date: Date, ...args: never[]) => Date | number, date: Date | undefined | null, ...args: unknown[]): Date | number | undefined | null;

/**
 * A function that will execute a Date function (usually a date-dns function) in the context of a timezone.
 * For example, the date-fns startOfDay returns a Date that is at the start of the current
 * day in the *current* timezone. Calling `inZone(<other timezone>, startOfDay, date)` makes
 * it work in the context of `<other timezone>`.
 *
 * **WARNING**: using with Date functions that don't operate on the local fields of a Date can produce undesired
 * results. An example is with date-fns addHours. If the system timezone is Pacific/Auckland then adding 1 hour to
 * 2am Sunday 3rd April 2022 in Australia/Brisbane will give back 2am Sunday 3rd April 2022 in Australia/Brisbane (the
 * same date). It is because under the covers the addHours operation is performed on 2am Sunday 3rd April 2022 in
 * Pacific/Auckland and the hour of day field remains at 2am.
 *
 * **Examples**
 *
 * Start of month in Australia/Brisbane for 2023-01-31T12:00:00Z:
 * ```
 * inZone('Australia/Brisbane', startOfMonth, new Date('2023-01-31T12:00:00Z'))
 * //=> 2022-12-31T14:00:00Z
 * ```
 * Start of month in Pacific/Auckland for 2023-01-31T12:00:00Z:
 * ```
 * inZone('Pacific/Auckland', startOfMonth, new Date('2023-01-31T12:00:00Z'))
 * //=> 2023-01-31T11:00:00Z
 * ```
 * Add 1 day in Australia/Brisbane to 2022-04-02T14:00:00Z:
 * ```
 * inZone('Australia/Brisbane', addDays, new Date('2022-04-02T14:00:00Z'), 1)
 * //=> 2022-04-03T14:00:00Z
 * ```
 * Add 1 day in Pacific/Auckland to 2022-04-02T11:00:00Z:
 * ```
 * inZone('Pacific/Auckland', addDays, new Date('2022-04-02T11:00:00Z'), 1)
 * //=> 2022-04-03T12:00:00Z
 * ```
 * Start of 2nd day of current month in Australia/Brisbane for 2023-01-31T12:00:00Z:
 * ```
 * inZone('Australia/Brisbane', (d) => addDays(startOfMonth(d), 1), new Date('2023-01-31T12:00:00Z'))
 * //=> 2023-01-01T14:00:00Z
 * ```
 * Get hour of day in Pacific/Auckland of 2022-04-02T13:59:00Z:
 * ```
 * inZone('Pacific/Auckland', getHours, new Date('2022-04-02T13:59:00Z'))
 * //=> 2
 * ```
 *
 * @param zone the timezone to operate in
 * @param fn the fn to run in the context of the timezone
 * @param date the date operate on
 * @param args arbitrary arguments that will also be passed to fn
 */
export function inZone(zone: string, fn: (date: Date, ...args: never[]) => Date | number, date: Date | undefined | null, ...args: unknown[]): Date | number | undefined | null {
  if (typeof date === 'undefined') {
    return undefined
  } else if (date === null) {
    return null
  } else {
    // in code below: utcToZonedTime, zonedTimeToUtc, and the supplied fns work in the context of the system timezone
    //
    // utcToZonedTime translates so the local date time becomes what was the local date time in the specified zone
    // Here are examples when the system timezone is Pacific/Auckland and starting date time is
    // Fri Feb 16 2024 01:00:00 GMT+1300 (New Zealand Daylight Time) (2024-02-15T12:00:00.000Z):
    //
    // zone             | zoned utc                | zoned local
    // Pacific/Auckland | 2024-02-15T12:00:00.000Z | Fri Feb 16 2024 01:00:00 GMT+1300 (New Zealand Daylight Time)
    // Australia/Sydney | 2024-02-15T10:00:00.000Z | Thu Feb 15 2024 23:00:00 GMT+1300 (New Zealand Daylight Time)
    // Etc/UTC          | 2024-02-14T23:00:00.000Z | Thu Feb 15 2024 12:00:00 GMT+1300 (New Zealand Daylight Time)
    // Pacific/Niue     | 2024-02-14T12:00:00.000Z | Thu Feb 15 2024 01:00:00 GMT+1300 (New Zealand Daylight Time)
    //
    // Notice that for Australia/Sydney, the zoned local is Feb 15 23:00. This is the local date time of
    // 2024-02-15T12:00:00.000Z (i.e. 2 hours behind)
    const zoned = utcToZonedTime(date, zone);
    // execute the function on the translated date time. The function operates on the system timezone but the date
    // we give it has been translated so that the local date of the date in the zone is now the local date of the date
    // in the system timezone
    const zonedResult = fn(zoned, ...args as never[]);
    if (typeof zonedResult === 'number') {
      return zonedResult;
    }
    // if the result is a date (not a number) then we need to translate it back so that the local date of the date in
    // the system timezone becomes the local date of the date in the zone. We use zonedTimeToUtc for that.
    // Here are examples when the system timezone is Pacific/Auckland and starting date time is
    // Thu Feb 15 2024 00:00:00 GMT+1300 (New Zealand Daylight Time) (2024-02-14T11:00:00.000Z):
    //
    // zone             | result
    // Pacific/Auckland | 2024-02-14T11:00:00.000Z
    // Australia/Sydney | 2024-02-14T13:00:00.000Z
    // Etc/UTC          | 2024-02-15T00:00:00.000Z
    // Pacific/Niue     | 2024-02-15T11:00:00.000Z
    //
    // For all the above examples, the local date time of the utc date time in the respective zone will be
    // Thu Feb 15 2024 00:00:00
    const result = zonedTimeToUtc(zonedResult, zone);
    return result;
  }
}
